import "./Dash.css";
import { Card, EmptyState } from "./Card";
import { StatusPill } from "./Status";
import { complexityText, minutesSince, priorityText } from "./IssueCard";
import type { Issue } from "../lib/tasks";
import { isMerging, isWorking } from "../lib/tasks";
import type { RunnerRow } from "../lib/auth";
import { runnerState } from "./Runner";
import { tx } from "../i18n";

/* ══ 대시보드 조각(t-in-port-dashboard) ══
   pr.b3x.app 대시보드를 옮겼다 — 지표 · 나루 · 진행률 · 주의 필요 · 프로젝트 카드. 전부 **보드가 읽는 같은
   목록(`GET /tasks`)** 을 접은 값이라 요청을 더 만들지 않는다(나루만 `GET /admin/runners`). 판정은 새로 하지
   않는다 — 「도는 중」은 열린 세션, 「병합 대기」는 review + PR 링크(lib/tasks 의 isWorking · isMerging) 그대로다.
   조립은 공통 .b-card · .b-stat · .b-meter · .b-dt 이고, b3x-in 전용은 나루 카드(b3x-naru) 하나다. */

const CLOSED = new Set(["done", "cancelled"]);
const LEAF = (t: Issue) => t.childCount === 0;

export type DashFacts = {
  /** 진행률 — **잎 이슈만** 센다. 상위 이슈까지 세면 같은 일을 두 번 센다. 취소는 분모에서 뺀다(「안 하기로 함」). */
  done: number;
  total: number;
  kpi: { open: number; running: number; blocked: number; review: number; merging: number; questions: number };
  attention: Issue[];
  projects: { id: string | null; open: Issue[] }[];
};

/** 목록을 대시보드 값으로 접는다. 순수 함수 — 패턴 페이지가 견본 목록으로 같은 화면을 세운다. */
export function dashFacts(list: Issue[]): DashFacts {
  const leaves = list.filter(LEAF);
  const open = list.filter((t) => !CLOSED.has(t.status));
  const openLeaves = open.filter(LEAF);
  const byProject = new Map<string | null, Issue[]>();
  for (const t of openLeaves) {
    const k = t.projectId ?? null;
    byProject.set(k, [...(byProject.get(k) ?? []), t]);
  }
  // 미분류(프로젝트 없음)는 늘 맨 뒤 — 버리지 않는다.
  const projects = [...byProject.entries()]
    .map(([id, rows]) => ({ id, open: rows }))
    .sort((a, b) => (a.id === null ? 1 : b.id === null ? -1 : b.open.length - a.open.length));
  return {
    done: leaves.filter((t) => t.status === "done").length,
    total: leaves.filter((t) => t.status !== "cancelled").length,
    kpi: {
      open: openLeaves.length,
      running: open.filter(isWorking).length,
      blocked: openLeaves.filter((t) => t.status === "blocked").length,
      review: openLeaves.filter((t) => t.status === "review" && !isMerging(t)).length,
      merging: openLeaves.filter(isMerging).length,
      questions: open.filter((t) => (t.openQuestions?.length ?? 0) > 0).length,
    },
    // 주의 필요 = 사람이 손대야 움직이는 것 둘 — 막힘과 답 안 한 질문. 오래 멈춘 것부터.
    attention: open
      .filter((t) => t.status === "blocked" || (t.openQuestions?.length ?? 0) > 0)
      .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt)),
    projects,
  };
}

/** 지표 — 한 카드 안 숫자 여섯. 숫자마다 낱말이 같이 간다. */
export function DashKpis({ k }: { k: DashFacts["kpi"] }) {
  const items: ["open" | "running" | "blocked" | "questions" | "review" | "merging", number][] = [
    ["open", k.open],
    ["running", k.running],
    ["blocked", k.blocked],
    ["questions", k.questions],
    ["review", k.review],
    ["merging", k.merging],
  ];
  return (
    <div className="b3x-dash-kpis">
      {items.map(([key, n]) => (
        <div key={key} className="b-stat">
          <span className="b3x-dash-kpis__label">{tx(`dashx.kpi.${key}`)}</span>
          <p className="b-stat-value">{n}</p>
        </div>
      ))}
    </div>
  );
}

type NaruRow = { name: string; state: "running" | "idle" | "down" | "never" | "unknown"; runs: Issue[]; since: string | null };

/** 나루 카드 줄 — 러너 표(관리자만 받는다)와 열린 세션을 합친다. 러너 표를 못 받았으면 세션이 있는 나루만
 *  그리고, 붙었는지는 「모름」이다 — 「끊김」으로 지어내지 않는다. */
export function naruRows(runners: RunnerRow[] | null, list: Issue[]): NaruRow[] {
  const runs = new Map<string, Issue[]>();
  for (const t of list) if (t.openSession) runs.set(t.openSession.runner, [...(runs.get(t.openSession.runner) ?? []), t]);
  const rows: NaruRow[] = (runners ?? []).map((r) => {
    const s = runnerState(r);
    const mine = runs.get(r.name) ?? [];
    runs.delete(r.name);
    return { name: r.name, state: mine.length ? "running" : s === "live" ? "idle" : s, runs: mine, since: s === "down" ? (r.downSince ?? r.lastSeenAt) : (r.presence?.since ?? null) };
  });
  for (const [name, mine] of runs) rows.push({ name, state: "running", runs: mine, since: null });
  if (!runners) for (const r of rows) if (r.state !== "running") r.state = "unknown";
  return rows;
}

const NARU_GLYPH: Record<NaruRow["state"], string> = { running: "◉", idle: "○", down: "✕", never: "◌", unknown: "?" };

export function NaruCards({ rows, now = Date.now(), note }: { rows: NaruRow[]; now?: number; note?: string }) {
  if (!rows.length) return <EmptyState compact icon="server" title={tx("dashx.naru.empty")} desc={note} />;
  return (
    <>
      {note ? <p className="b3x-dash-note">{note}</p> : null}
      <div className="b3x-naru-grid">
        {rows.map((r) => (
          <article key={r.name} className={`b3x-naru is-${r.state}`}>
            <header className="b3x-naru__head">
              <span className="b3x-naru__name">{r.name}</span>
              <span className="b3x-naru__state">
                <span aria-hidden="true">{NARU_GLYPH[r.state]}</span> {tx(`dashx.naru.state.${r.state}`)}
              </span>
            </header>
            {r.runs.length ? (
              <ul className="b3x-naru__runs">
                {r.runs.map((t) => (
                  <li key={t.id}>
                    <span className="tnum">{t.id}</span> {t.title}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="b3x-naru__idle">{tx(r.state === "idle" ? "dashx.naru.idleLine" : "dashx.naru.noRun")}</p>
            )}
            <p className="b3x-naru__since">
              {r.since
                ? tx(r.state === "down" ? "dashx.naru.downFor" : "dashx.naru.since", { t: minutesSince(r.since, now) })
                : tx("dashx.naru.sinceUnknown")}
            </p>
          </article>
        ))}
      </div>
    </>
  );
}

/** 진행률 — 막대만으로 말하지 않는다(머리 줄에 숫자). 분모가 0 이면 막대를 안 그린다(0% 는 거짓이다). */
export function DashProgress({ done, total, label }: { done: number; total: number; label?: string }) {
  if (!total) return <p className="b3x-dash-note">{tx("dashx.progress.none")}</p>;
  const pct = Math.round((done / total) * 100);
  return (
    <div className="b-meter is-success">
      <div className="b-meter-head">
        <span>{label ?? tx("dashx.progress.label")}</span>
        <span>{tx("dashx.progress.value", { pct, done, total })}</span>
      </div>
      <div className="b-meter-track" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <span className="b-meter-bar" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** 주의 필요 — 왼쪽 막대(상태색) + 상태 칩(글자) · 사유 · 담당 · 멈춘 지. 줄을 누르면 상세. */
export function AttentionList({ rows, onOpen, now = Date.now() }: { rows: Issue[]; onOpen: (id: string) => void; now?: number }) {
  if (!rows.length) return <EmptyState compact icon="check" title={tx("dashx.attention.empty")} />;
  return (
    <ul className="b3x-attn">
      {rows.map((t) => {
        const q = t.openQuestions?.[0];
        const why = q ? tx("dashx.attention.question", { q: q.prompt }) : t.blockedNote ?? tx("issueActs.blockedNoteNone");
        return (
          <li key={t.id}>
            <button type="button" className={`b3x-attn__row is-${t.status}`} onClick={() => onOpen(t.id)}>
              <span className="b3x-attn__top">
                <StatusPill status={t.status} working={isWorking(t)} />
                <span className="b3x-attn__title">{t.title}</span>
                <span className="b3x-attn__age tnum">{minutesSince(t.updatedAt, now)}</span>
              </span>
              <span className="b3x-attn__why">{why}</span>
              <span className="b3x-attn__meta">
                <span className="tnum">{t.id}</span> · {tx("board.detail.assignee")} {t.assignee ?? tx("common.state.none")}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

const GROUPS: { key: "doing" | "blocked" | "todo"; has: (t: Issue) => boolean }[] = [
  { key: "doing", has: (t) => t.status === "in-progress" || t.status === "review" },
  { key: "blocked", has: (t) => t.status === "blocked" },
  { key: "todo", has: (t) => t.status === "todo" || t.status === "ready" },
];
/** 한 무리에 그리는 줄 상한 — 넘치면 「n건 더」로 보드에 넘긴다(대시보드는 요약이다). */
export const GROUP_MAX = 5;

/** 프로젝트 카드 — 열린 잎 이슈를 진행중 · 막힘 · 할 일 셋으로. 줄은 id · 제목 · 복잡도 · 등급 · 단계. */
export function ProjectCards({
  projects,
  names,
  onOpen,
}: {
  projects: DashFacts["projects"];
  names: Record<string, string>;
  onOpen: (id: string) => void;
}) {
  if (!projects.length) return <EmptyState icon="inbox" title={tx("dashx.projects.empty")} desc={tx("dashx.projects.emptyDesc")} />;
  return (
    <div className="b3x-pgrid">
      {projects.map((p) => (
        <Card
          key={p.id ?? "-"}
          title={p.id === null ? tx("dashx.projects.none") : (names[p.id] ?? p.id)}
          meta={tx("dashx.projects.open", { n: p.open.length })}
        >
          {GROUPS.map((g) => {
            const rows = p.open.filter(g.has);
            if (!rows.length) return null;
            return (
              <div key={g.key} className="b3x-pgrid__group">
                <span className="b-sidenav-group">{tx(`dashx.projects.group.${g.key}`, { n: rows.length })}</span>
                <div className="b-dt">
                  {rows.slice(0, GROUP_MAX).map((t) => (
                    <button key={t.id} type="button" className="b-dt-row is-interactive b3x-pgrid__row" onClick={() => onOpen(t.id)}>
                      <span className="b-dt-cell is-mono b3x-pgrid__id">{t.id}</span>
                      <span className="b-dt-cell is-primary b3x-pgrid__title">{t.title}</span>
                      <span className="b-dt-cell b3x-pgrid__tags">
                        <span className="b-badge">{complexityText(t.complexity)}</span>
                        <span className="b-badge">{priorityText(t.priority)}</span>
                        <StatusPill status={t.status} working={isWorking(t)} />
                      </span>
                    </button>
                  ))}
                </div>
                {rows.length > GROUP_MAX ? (
                  <a className="b3x-pgrid__more" href="#/board">
                    {tx("dashx.projects.more", { n: rows.length - GROUP_MAX })}
                  </a>
                ) : null}
              </div>
            );
          })}
        </Card>
      ))}
    </div>
  );
}
